import {
  COMPONENT_RENDERING_URL,
  get_headers,
  get_token,
  verify_super_user_status
} from '@/utils/constants/constants.js';
import alert_mixin from '@/utils/mixins/alert_mixin';
import axios from 'axios';

export default {
  name: "ProbaWorkflowsSidebar",
  mixins: [alert_mixin],
  data: () => ({
    menu_items: [],
    headers: {},
    token: "",
    is_sidebar_hidden: false,
    is_loading: false,
    role: sessionStorage.getItem('user_role') || null,
    entity_type_id: sessionStorage.getItem('entity_type_id'),
    base_entity_type_id: sessionStorage.getItem('base_entity_type_id'),
    project_id: sessionStorage.getItem('project_id') || null,
    is_super_user: false,
    // Use a composite key: "type:id" where type is 'sub' or 'child'
    active_item_key: null,
    currently_logged_in: sessionStorage.getItem('lead_name'),
    entity_name: sessionStorage.getItem('entity_name'),
    entity_type_name: sessionStorage.getItem('entity_type_name'),
  }),
  mounted() {
    this.headers = get_headers();
    this.token = get_token();

    if (!this.entity_type_name) {
    this.entity_type_name =
      this.$route?.params?.entity_type_name ||
      this.$route?.query?.entity_type_name ||
      null;
  }

    this.active_item_key = sessionStorage.getItem('sidebar_active_item_key') || null;
    this.fetch_sidebar().then(() => {
      // Expand parent menu after sidebar is loaded
      this.$nextTick(() => {
        this.expand_active_parent();
      });
    });
    this.check_super_user();
    window.addEventListener('storage', this.sync_session_state);
    window.addEventListener('session-storage-updated', this.sync_session_state);
  },
  computed: {
    is_lead() {
      // Get the stored value (will be "true", "false", or null if not set)
      const is_lead_flag = sessionStorage.getItem('is_lead');
      // If the flag exists, convert to boolean
      if (is_lead_flag !== null) {
        return is_lead_flag === 'true';
      }
      // Fallback - check lead_id directly if flag doesn't exist
      const lead_id = sessionStorage.getItem('lead_id');
      return lead_id !== null && lead_id !== 'null';
    },

    should_hide_logout() {
      // List of paths where the logout button should be hidden
      const hiddenPaths = ['/view_user_trust_form', '/view_all_user_forms'];
      return hiddenPaths.includes(this.$route.path);
    },

    should_show_template() {
      const allowed_routes = [
        '/company_management',
        '/configure_company_management',
        '/user_management',
        '/roles_management',
        '/permission_management',
        '/action_management',
        '/assigning_permissions',
        '/creating_a_policy',
        '/manage_entity_type',
        '/manage_questions',
        '/view_template',
        '/signing_workflow',
        '/form_management',
        '/status_management',
        '/data_points',
        '/manage_risks',
        '/create_enquiry_stages',
        '/view_entity_type',
        '/create_entity',
        '/configure_template',
        '/manage_assigned_workflows',
        '/upload_template',
        '/risk_questions',
        '/create_enquiry_form',
        '/assign_questions_to_enquiry_form',
        '/services',
        '/package_services',
        '/verification_services_configurations',
        '/compliance_configurations',
        '/ongoing_compliance_checklist',
        '/condition_management'
      ];
      return allowed_routes.includes(this.$route.path);
    },

    should_show_entity_menu() {
      const allowedRoutes = [
        '/view_user_trust_form',
        '/view_all_user_forms',
        '/requested_information_update',
        '/unresolved_request',
        '/resolved_requests',
        '/enquiry_chat',
        '/entity_application_overview',
        '/application_overview_details',
        '/auto_verification_overview',
        '/entity_overview',
        '/view_contracts',
        '/entity_risk_assessment',
        '/answer_risk_assessment',
        '/lead_questionnaire',
        '/lead_questionnaire_form',
        '/entity_enquiry_management',
        '/proof_of_payments',
        '/trustee_view_dashboard',
        '/asset_registry',
        '/view_trustees',
        '/entity_documents',
        '/compliance_overview',
        '/view_beneficiaries',
        '/beneficiary_dashboard',
        '/beneficiary_distributions'

      ]

      return allowedRoutes.includes(this.$route.path)
    },

    sorted_menu_items() {
      const safeComponents = (this.menu_items || []).map(component => {
        // Create safe component copy using Object.assign
        const safeComponent = Object.assign({}, component || {});

        // Process sub-components
        safeComponent.sub_components = (component.sub_components || [])
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map(sub => {
            // Create safe sub-component copy
            const safeSub = Object.assign({}, sub || {});

            // Process child components
            safeSub.child_components = (sub.child_components || [])
              .slice()
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

            return safeSub;
          });

        return safeComponent;
      });

      return safeComponents;
    }
  },
  methods: {
    sync_session_state() {
      this.role = sessionStorage.getItem('user_role') || null;
      this.currently_logged_in = sessionStorage.getItem('lead_name');
      this.project_id = sessionStorage.getItem('project_id') || null;
    },

    generate_item_key(item, type) {
      return `${type}:${item.id}`;
    },

    is_active(item, type) {
      if (!this.active_item_key || !item?.id) {
        return false;
      }
      const item_key = this.generate_item_key(item, type);
      return this.active_item_key === item_key;
    },

    set_active_item(item, type, has_children = false) {
      if (has_children) {
        return;
      }

      const new_key = this.generate_item_key(item, type);
      this.active_item_key = new_key;
      sessionStorage.setItem('sidebar_active_item_key', new_key);
    },

    set_hardcoded_active(route_name) {
      const new_key = `hardcoded:${route_name}`;
      this.active_item_key = new_key;
      sessionStorage.setItem('sidebar_active_item_key', new_key);
    },

    is_hardcoded_active(route_name) {
      return this.active_item_key === `hardcoded:${route_name}`;
    },

    parse_item_url(url) {
      const origin = window.location?.origin ?? "/";
      return !url
        ? { path: "/", query: {} }
        : (({ pathname, searchParams }) => ({
            path: pathname,
            query: Object.fromEntries(searchParams),
          }))(new URL(url, origin));
    },


    get_entity_type_id(item, parent, { child_only = false } = {}) {
      // Prefer common key names on the item (child). Do NOT fall back to parent when child_only=true.
      const childId =
        item?.entity_type_id ??
        item?.entity_type ??
        item?.entityTypeId ??
        item?.entityType ??
        null;

      if (child_only) return childId;

      const parentId =
        parent?.entity_type_id ??
        parent?.entity_type ??
        parent?.entityTypeId ??
        parent?.entityType ??
        null;

      return childId ?? parentId ?? null;
    },


    get_status_from_item(item) {
      // status id from backend (FormStatus.id)
      if (item?.status !== undefined && item?.status !== null) {
        return String(item.status);
      }

      // fallback: get ?status=value from URL
      const base = this.parse_item_url(item?.url || '');
      return base.query?.status ?? null;
    },


    build_route_location(item, parent, child_only = false) {
      const base = this.parse_item_url(item?.url || '/');
      const route = { ...base, query: { ...base.query } };

      // entity_type_id
      const entity_type_id = this.get_entity_type_id(item, parent, { child_only });
      if (entity_type_id) {
        route.query.entity_type_id = String(entity_type_id);
      }

      // status ID (from backend)
      const status = this.get_status_from_item(item);
      if (status) {
        route.query.status_id = String(status);
      }

      const display_name = item?.display_name ?? parent?.display_name ?? null;
      if (display_name) {
        sessionStorage.setItem('display_name', String(display_name));
      } else {
        sessionStorage.removeItem('display_name');
      }
      return route;
    },

    expand_active_parent() {
      if (!this.active_item_key) return;

      // Parse the active key to find the child
      const [type, id] = this.active_item_key.split(':');

      if (type !== 'child') return;

      // Find the parent that contains this active child
      for (const component of this.sorted_menu_items) {
        for (const sub of component.sub_components || []) {
          const child = (sub.child_components || []).find(
            comp => String(comp.id) === id
          );

          if (child) {
            // Programmatically add 'show' class to expand the collapse menu
            const collapse_element = document.getElementById(`collapse-${sub.id}`);
            if (collapse_element) {
              collapse_element.classList.add('show');
            }
            return;
          }
        }
      }
    },

    fetch_sidebar() {
      return this.show_loader("Loading menu...")
        .then(loader => {
          this.is_loading = true;
          const params = {
            role: this.role,
            base_entity_type_id: this.base_entity_type_id,
            project_id: this.project_id,
          };

          return axios.get(COMPONENT_RENDERING_URL, {
            headers: this.headers,
            params
          })
            .then(response => {
              this.menu_items = response.data.data;
              return this.close_loader(loader);

            })
            .catch(error => {
              console.warn("Error fetching sidebar items:", error)
              return this.close_loader(loader);
            })
            .finally(() => {
              this.is_loading = false;
            });
        });
    },

    is_status_active(url) {
      if (!/^https?:\/\/|^\//.test(url)) return false;

      const new_url = new URL(url, window.location.origin);
      const target = (new_url.searchParams.get('status') || '').trim().toLowerCase();
      const current = (this.$route.query.status || '').trim().toLowerCase();

      const norm = s => s.replace(/[\s_]+/g, '-');
      return !!target && norm(target) === norm(current);
    },

    view_pending_applications(entity_type_name = null) {
      if (entity_type_name !== null) {
        sessionStorage.setItem('entity_type_name', entity_type_name);
      } else {
        sessionStorage.removeItem('entity_type_name');
      }
      // navigate to the same route…
      this.$router.push({ path: '/pending_applications' })
    },

    logout() {
      localStorage.clear();
      sessionStorage.clear();
      this.$router.push('/');
    },

    async check_super_user() {
      this.is_super_user = await verify_super_user_status();
    },

  },
  watch: {
    $route() {
      this.sync_session_state();
      this.$forceUpdate();
    }
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.sync_session_state);
    window.removeEventListener('session-storage-updated', this.sync_session_state);
  }
};